import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import ReviewAnalysis from './ReviewAnalysis';
import axiosInstance from '../axiosConfig';
import {
  Typography,
  Box,
  Button,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  CssBaseline,
  Stack,
  Container,
  Fade,
  Grid,
  Chip
} from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';
import { styled } from '@mui/material/styles';
import AppTheme from './shared-theme/AppTheme';
import ColorModeSelect from './shared-theme/ColorModeSelect';
import InsightsIcon from '@mui/icons-material/Insights';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import StarIcon from '@mui/icons-material/Star';
import UpdateIcon from '@mui/icons-material/Update';

const InsightContainer = styled(Stack)(({ theme }) => ({
  minHeight: '100dvh',
  position: 'relative',
  '&::before': {
    content: '""',
    display: 'block',
    position: 'fixed',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
  minHeight: '100dvh',
  width: '100%',
  position: 'relative',
  padding: 0,
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
  [theme.breakpoints.up('sm')]: {
    padding: 0,
  },
}));

const TimeFrameSelect = styled(FormControl)(({ theme }) => ({
  minWidth: 100,
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem',
    transform: 'translate(14px, 12px)',
    '&.MuiInputLabel-shrink': {
      transform: 'translate(14px, -9px) scale(0.75)',
    },
  },
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    '& .MuiSelect-select': {
      padding: '8px 14px',
    },
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const TimeFrameWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const BackButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  color: theme.palette.text.secondary,
  padding: theme.spacing(1, 1),
  marginBottom: theme.spacing(3),
  alignSelf: 'flex-start',
  '&:hover': {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
  },
}));


const MetricCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  minWidth: 200,
}));

const TIME_FRAMES = {
  WEEK: '7 Days',
  MONTH: '30 Days',
  HALF_YEAR: '6 Months',
  ALL: 'All Time'
};

const ChartContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  height: 300,
  width: '100%',
  '& .MuiLineElement-root': {
    strokeWidth: 2,
  },
  '& .MuiChartsAxis-line': {
    stroke: theme.palette.divider,
  },
  '& .MuiChartsAxis-tick': {
    stroke: theme.palette.divider,
  },
  '& .MuiChartsAxis-grid': {
    stroke: theme.palette.divider,
    strokeDasharray: '3 3',
    strokeOpacity: 0.2,
  },
}));

const UpdateReminder = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const ReviewInsight = () => {
  const { qrCodeId } = useParams();
  const location = useLocation();
  const placeId = location.state?.placeId;
  
  // Add refs to track API calls
  const scanDataFetched = useRef(false);
  const reviewDataFetched = useRef(false);
  const analysisDataFetched = useRef(false);
  
  // Time frame state
  const [selectedTimeFrame, setSelectedTimeFrame] = useState(TIME_FRAMES.WEEK);
  
  // Scan data states
  const [rawScanData, setRawScanData] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  // Review data states
  const [rawReviewData, setRawReviewData] = useState(null);
  const [reviewChartData, setReviewChartData] = useState(null);
  const [reviewSummary, setReviewSummary] = useState(null);
  const [isReviewsLoading, setIsReviewsLoading] = useState(true);
  const [reviewsError, setReviewsError] = useState('');

  const [analysisData, setAnalysisData] = useState('');
  const [isAnalysisLoading, setIsAnalysisLoading] = useState(true);
  const [analysisError, setAnalysisError] = useState('');

  const [nextUpdateTime, setNextUpdateTime] = useState(null);

  // Helper function to aggregate data based on time frame
  const aggregateDataByTimeFrame = (data, timeFrame) => {
    const now = new Date();
    let startDate = new Date();
    let granularity = 'day';

    switch (timeFrame) {
      case TIME_FRAMES.WEEK:
        startDate.setDate(now.getDate() - 7);
        granularity = 'day';
        break;
      case TIME_FRAMES.MONTH:
        startDate.setDate(now.getDate() - 30);
        granularity = 'week';
        break;
      case TIME_FRAMES.HALF_YEAR:
        startDate.setMonth(now.getMonth() - 6);
        granularity = 'month';
        break;
      case TIME_FRAMES.ALL:
        startDate = new Date(Math.min(...data.map(d => d.date)));
        granularity = 'month';
        break;
      default:
        startDate.setDate(now.getDate() - 7);
        granularity = 'day';
        break;
    }

    // Filter data within the time range
    let filteredData = data.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate >= startDate && itemDate <= now;
    });

    // Aggregate data based on granularity
    const aggregated = new Map();

    // Aggregate data into points
    filteredData.forEach(item => {
      let key;
      const itemDate = new Date(item.date);

      if (granularity === 'day') {
        key = itemDate.toISOString().split('T')[0];
      } else if (granularity === 'week') {
        const day = itemDate.getDay();
        const diff = itemDate.getDate() - day + (day === 0 ? -6 : 1);
        const monday = new Date(itemDate.setDate(diff));
        key = monday.toISOString().split('T')[0];
      } else {
        key = new Date(itemDate.getFullYear(), itemDate.getMonth(), 1)
          .toISOString().split('T')[0];
      }

      if (!aggregated.has(key)) {
        aggregated.set(key, {
          date: new Date(key),
          scans: 0,
          rating: 0,
          count: 0,
          totalRating: 0,
          dataPoints: 0,
          items: []
        });
      }

      const point = aggregated.get(key);
      point.items.push(item);

      if ('scans' in item) {
        point.scans += item.scans;
      }
      if ('rating' in item) {
        point.totalRating += item.rating;
        point.dataPoints += 1;
      }
      if ('count' in item) {
        point.count = Math.max(point.count, item.count); // Use the highest count in the period
      }
    });

    // Calculate final values and convert to array
    const result = Array.from(aggregated.values())
      .map(point => ({
        date: point.date,
        scans: point.scans,
        rating: point.dataPoints > 0 ? point.totalRating / point.dataPoints : null,
        count: point.count
      }))
      .sort((a, b) => a.date - b.date);

    // Ensure we have at least two data points for metrics calculation
    if (result.length === 1) {
      // If we only have one point, duplicate it as the previous period
      const singlePoint = result[0];
      const previousDate = new Date(singlePoint.date);
      
      if (granularity === 'day') {
        previousDate.setDate(previousDate.getDate() - 1);
      } else if (granularity === 'week') {
        previousDate.setDate(previousDate.getDate() - 7);
      } else {
        previousDate.setMonth(previousDate.getMonth() - 1);
      }
      
      result.unshift({
        date: previousDate,
        scans: 0,
        rating: singlePoint.rating,
        count: singlePoint.count
      });
    }

    return result;
  };

  // Memoize the metrics calculation
  const metrics = useMemo(() => {
    if (!rawReviewData || !rawScanData) return null;

    // Get aggregated data based on selected time frame
    const aggregatedReviewData = aggregateDataByTimeFrame(rawReviewData, selectedTimeFrame);
    const aggregatedScanData = aggregateDataByTimeFrame(rawScanData, selectedTimeFrame);

    if (aggregatedReviewData.length < 2) return null;

    const lastPeriod = aggregatedReviewData[aggregatedReviewData.length - 1] || {};
    const firstPeriod = aggregatedReviewData[0] || {};
    
    const ratingChange = firstPeriod.rating 
      ? ((lastPeriod.rating - firstPeriod.rating) / firstPeriod.rating) * 100 
      : 0;
    
    const reviewChange = firstPeriod.count 
      ? ((lastPeriod.count - firstPeriod.count) / firstPeriod.count) * 100 
      : 0;
    
    return {
      currentRating: lastPeriod.rating || 0,
      totalReviews: lastPeriod.count || 0,
      ratingChange: ratingChange || 0,
      reviewChange: reviewChange || 0,
      totalScans: aggregatedScanData.reduce((sum, item) => sum + (item.scans || 0), 0),
    };
  }, [rawReviewData, rawScanData, selectedTimeFrame]);

  // Update chart data when time frame changes
  useEffect(() => {
    if (rawScanData) {
      const aggregatedData = aggregateDataByTimeFrame(rawScanData, selectedTimeFrame);
      
      setChartData({
        dataset: aggregatedData || [],  // Ensure dataset is never undefined
        series: [{
          dataKey: 'scans',
          label: 'Scans',
          color: '#1976d2',
          valueFormatter: (value) => `${value} scan${value !== 1 ? 's' : ''}`,
        }],
      });
    }

    if (rawReviewData) {
      const aggregatedData = aggregateDataByTimeFrame(rawReviewData, selectedTimeFrame);
      
      // Calculate min count for axis
      const minCount = Math.min(...aggregatedData.map(d => d.count));
      const countAxisMin = Math.floor(minCount / 2);
      
      setReviewChartData({
        dataset: aggregatedData || [],  // Ensure dataset is never undefined
        series: [
          {
            dataKey: 'rating',
            label: 'Average Rating',
            color: '#ff6d75',
            yAxisKey: 'ratingAxis',
            valueFormatter: (value) => value?.toFixed(1) || '0.0',
          },
          {
            dataKey: 'count',
            label: 'Review Count',
            color: '#82ca9d',
            yAxisKey: 'countAxis',
          },
        ],
        countAxisMin, // Store min value for count axis
      });
    }
  }, [selectedTimeFrame, rawScanData, rawReviewData]);

  useEffect(() => {
    const fetchScanData = async () => {
      if (scanDataFetched.current) return; // Skip if already fetched
      
      try {
        const response = await axiosInstance.post('/get-qr-scan', { qrCodeId });

        // Transform scan data
        const dataset = Object.entries(response.data.scanRecords).map(([date, scans]) => ({
          date: new Date(date),
          scans,
        })).sort((a, b) => a.date - b.date);

        setRawScanData(dataset);
        scanDataFetched.current = true; // Mark as fetched
      } catch (err) {
        setError('No scan data available, ask for customer to scan or tap to leave a review!');
        console.error('Error fetching scan data:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchScanData();
  }, [qrCodeId]);

  useEffect(() => {
    const fetchReviewData = async () => {
      if (!placeId || reviewDataFetched.current) {
        if (!placeId) {
          setReviewsError('Place ID is required to load review data.');
          setIsReviewsLoading(false);
        }
        return;
      }

      try {
        const response = await axiosInstance.post('/google-reviews', { placeId });

        const { ratingHistory, reviewCountHistory, reviewSummary } = response.data;
        
        // Transform review data
        const dataset = Object.keys(ratingHistory || {})
          .map(date => ({
            date: new Date(date),
            rating: ratingHistory[date] || 0,
            count: reviewCountHistory?.[date] || 0,
          }))
          .filter(item => item.date && !isNaN(item.date.getTime())) // Filter out invalid dates
          .sort((a, b) => a.date - b.date);

        setRawReviewData(dataset);
        // Ensure reviewSummary has all required properties
        setReviewSummary(reviewSummary ? {
          rating: reviewSummary.rating || 0,
          totalReviews: reviewSummary.totalReviews || 0,
          recentReviews: reviewSummary.recentReviews || 0
        } : null);
        reviewDataFetched.current = true; // Mark as fetched
      } catch (err) {
        setReviewsError('Review data is not available yet, please check later');
      } finally {
        setIsReviewsLoading(false);
      }
    };

    fetchReviewData();
  }, [placeId]);

  useEffect(() => {
    const fetchAnalysis = async () => {
      if (!placeId || !reviewSummary || analysisDataFetched.current) return;

      try {
        setIsAnalysisLoading(true);
        const response = await axiosInstance.post('/get-review-analysis', { placeId });
        
        setAnalysisData({
          analysis: response.data.analysis,
          lastAnalysisTimestamp: response.data.lastAnalysisTimestamp || new Date().toISOString()
        });
        analysisDataFetched.current = true; // Mark as fetched
      } catch (err) {
        console.error('Analysis fetch error:', err);
        setAnalysisError(`Analysis is not available yet, please check tomorrow`);
      } finally {
        setIsAnalysisLoading(false);
      }
    };

    fetchAnalysis();
  }, [placeId, reviewSummary]);

  useEffect(() => {
    if (analysisData?.lastAnalysisTimestamp) {
      // If analysis exists, set next update to lastAnalysisTimestamp + 8 days
      const lastAnalysisTime = new Date(analysisData.lastAnalysisTimestamp);
      const nextUpdate = new Date(lastAnalysisTime);
      nextUpdate.setDate(nextUpdate.getDate() + 8);
      setNextUpdateTime(nextUpdate);
    } else {
      // If no analysis, set next update to 2 days from now
      const nextUpdate = new Date();
      nextUpdate.setDate(nextUpdate.getDate() + 2);
      setNextUpdateTime(nextUpdate);
    }
  }, [analysisData]);

  return (
    <AppTheme>
      <CssBaseline enableColorScheme />
      <InsightContainer>
        <StyledPaper>
          <Container maxWidth="lg" sx={{ py: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <ColorModeSelect />
            </Box>
            <Fade in timeout={800}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <BackButton
                  component={Link}
                  to="/profile"
                  startIcon={<ArrowBackIcon />}
                >
                  Back to Stores
                </BackButton>

                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: 'column',
                  gap: 2,
                  mb: 3,
                }}>
                  <Typography
                    variant="h5"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      fontWeight: 600,
                    }}
                  >
                    <InsightsIcon color="primary" sx={{ fontSize: 28 }} />
                    Review Insights
                  </Typography>

                  <TimeFrameWrapper>
                    <TimeFrameSelect size="small">
                      <InputLabel id="time-frame-select-label">Time Frame</InputLabel>
                      <Select
                        labelId="time-frame-select-label"
                        id="time-frame-select"
                        value={selectedTimeFrame}
                        label="Time Frame"
                        onChange={(e) => setSelectedTimeFrame(e.target.value)}
                      >
                        {Object.values(TIME_FRAMES).map((frame) => (
                          <MenuItem key={frame} value={frame}>
                            {frame}
                          </MenuItem>
                        ))}
                      </Select>
                    </TimeFrameSelect>
                  </TimeFrameWrapper>
                </Box>

                {/* New Dashboard Summary Section */}
                <Box sx={{ mb: 4 }}>
                  <Grid container spacing={0}>
                    <Grid item xs={12} md={3}>
                      <MetricCard elevation={0}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Average Rating
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <StarIcon color="primary" />
                          <Typography variant="h4" fontWeight="bold">
                            {metrics?.currentRating?.toFixed(1) || '0.0'}
                          </Typography>
                          {metrics?.ratingChange >= 0 ? (
                            <TrendingUpIcon color="success" />
                          ) : (
                            <TrendingDownIcon color="error" />
                          )}
                        </Box>
                        <Typography variant="caption" color={metrics?.ratingChange >= 0 ? 'success.main' : 'error.main'}>
                          {metrics?.ratingChange?.toFixed(1)}% change in this period
                        </Typography>
                      </MetricCard>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <MetricCard elevation={0}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Total Reviews
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h4" fontWeight="bold">
                            {metrics?.totalReviews || 0}
                          </Typography>
                          {metrics?.reviewChange >= 0 ? (
                            <TrendingUpIcon color="success" />
                          ) : (
                            <TrendingDownIcon color="error" />
                          )}
                        </Box>
                        <Typography variant="caption" color={metrics?.reviewChange >= 0 ? 'success.main' : 'error.main'}>
                          {metrics?.reviewChange?.toFixed(1)}% change in this period
                        </Typography>
                      </MetricCard>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <MetricCard elevation={0}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Total Scans
                        </Typography>
                        <Typography variant="h4" fontWeight="bold">
                          {metrics?.totalScans || 0}
                        </Typography>
                      </MetricCard>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ mb: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <QueryStatsIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Scans or Taps
                    </Typography>
                  </Box>
                  {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : error ? (
                    <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                  ) : chartData?.dataset.length > 0 ? (
                    <ChartContainer>
                      <LineChart
                        dataset={chartData.dataset}
                        margin={{ top: 20, right: 20, bottom: 40, left: 40 }}
                        xAxis={[{
                          dataKey: 'date',
                          scaleType: 'time',
                          min: Math.min(...chartData.dataset.map(d => new Date(d.date))),
                          max: Math.max(...chartData.dataset.map(d => new Date(d.date))),
                          tickMinStep: 24 * 3600 * 1000,
                          valueFormatter: (date) => {
                            const formatter = new Intl.DateTimeFormat('en-US', {
                              month: 'short',
                              day: 'numeric',
                            });
                            return formatter.format(date);
                          },
                          tickLabelStyle: {
                            angle: 45,
                            textAnchor: 'start',
                            fontSize: 12
                          }
                        }]}
                        yAxis={[{
                          tickNumber: 5,
                          tickLabelStyle: {
                            fontSize: 12
                          },
                          gridLines: {
                            display: true,
                            color: 'rgba(0,0,0,0.1)',
                          }
                        }]}
                        series={[{
                          ...chartData.series[0],
                          area: true,
                          showMark: true,
                          gradient: true,
                          curve: 'monotoneX',
                          lineStyle: { strokeWidth: 2 },
                          valueFormatter: (value) => `${value} scans`,
                        }]}
                        tooltip={{ trigger: 'axis' }}
                        slotProps={{
                          legend: {
                            hidden: true
                          },
                          popper: {
                            sx: {
                              '& .MuiChartsTooltip-paper': {
                                border: '1px solid grey',
                                transform: 'translateY(-80px)',
                              },
                            },
                          },
                        }}
                      />
                    </ChartContainer>
                  ) : (
                    <Alert severity="info">No scan data available for the selected time frame.</Alert>
                  )}
                </Box>

                <Box sx={{ mb: 6 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                    <AnalyticsIcon color="primary" />
                    <Typography variant="subtitle1" fontWeight={600}>
                      Review Statistics
                    </Typography>
                  </Box>
                  {isReviewsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                      <CircularProgress />
                    </Box>
                  ) : reviewsError ? (
                    <Alert severity="error" sx={{ mb: 2 }}>{reviewsError}</Alert>
                  ) : reviewChartData?.dataset.length > 0 ? (
                    <>
                      <ChartContainer sx={{ mb: 4 }}>
                        <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
                          Average Rating Over Time
                        </Typography>
                        <LineChart
                          dataset={reviewChartData.dataset}
                          margin={{ top: 20, right: 20, bottom: 40, left: 40 }}
                          xAxis={[{
                            dataKey: 'date',
                            scaleType: 'time',
                            min: Math.min(...reviewChartData.dataset.map(d => new Date(d.date))),
                            max: Math.max(...reviewChartData.dataset.map(d => new Date(d.date))),
                            tickMinStep: 24 * 3600 * 1000,
                            valueFormatter: (date) => {
                              const formatter = new Intl.DateTimeFormat('en-US', {
                                month: 'short',
                                day: 'numeric',
                              });
                              return formatter.format(date);
                            },
                            tickLabelStyle: {
                              angle: 45,
                              textAnchor: 'start',
                              fontSize: 12
                            }
                          }]}
                          yAxis={[{
                            min: 0,
                            max: 5,
                            tickNumber: 6,
                            gridLines: {
                              display: true,
                              color: 'rgba(0,0,0,0.1)',
                            }
                          }]}
                          series={[{
                            dataKey: 'rating',
                            label: 'Rating',
                            color: '#ff6d75',
                            area: true,
                            showMark: true,
                            gradient: true,
                            curve: 'monotoneX',
                            valueFormatter: (value) => value?.toFixed(1) || '0.0',
                            lineStyle: { strokeWidth: 2 }
                          }]}
                          tooltip={{
                            trigger: 'axis',
                            formatter: (params) => `Rating: ${params[0].value?.toFixed(1) || '0.0'}`
                          }}
                          slotProps={{
                            legend: {
                              hidden: true
                            },
                            popper: {
                              sx: {
                                '& .MuiChartsTooltip-paper': {
                                  border: '1px solid grey',
                                  transform: 'translateY(-80px)',
                                },
                              },
                            },
                          }}
                        />
                      </ChartContainer>
                      <ChartContainer>
                        <Typography variant="subtitle2" sx={{ mt: 8, mb: 2, color: 'text.secondary' }}>
                          Total Reviews Over Time
                        </Typography>
                        <LineChart
                          dataset={reviewChartData.dataset}
                          margin={{ top: 20, right: 20, bottom: 40, left: 40 }}
                          xAxis={[{
                            dataKey: 'date',
                            scaleType: 'time',
                            min: Math.min(...reviewChartData.dataset.map(d => new Date(d.date))),
                            max: Math.max(...reviewChartData.dataset.map(d => new Date(d.date))),
                            tickMinStep: 24 * 3600 * 1000,
                            valueFormatter: (date) => {
                              const formatter = new Intl.DateTimeFormat('en-US', {
                                month: 'short',
                                day: 'numeric',
                              });
                              return formatter.format(date);
                            },
                            tickLabelStyle: {
                              angle: 45,
                              textAnchor: 'start',
                              fontSize: 12
                            }
                          }]}
                          yAxis={[{
                            tickNumber: 5,
                            max: Math.ceil(Math.max(...reviewChartData.dataset.map(d => d.count)) * 1.3),
                            gridLines: {
                              display: true,
                              color: 'rgba(0,0,0,0.1)',
                            }
                          }]}
                          series={[{
                            dataKey: 'count',
                            label: 'Review Count',
                            color: '#82ca9d',
                            area: true,
                            showMark: true,
                            gradient: true,
                            curve: 'monotoneX',
                            valueFormatter: (value) => `${value} reviews`,
                            lineStyle: { strokeWidth: 2 }
                          }]}
                          tooltip={{
                            trigger: 'axis',
                            formatter: (params) => `Reviews: ${params[0].value}`
                          }}
                          slotProps={{
                            legend: {
                              hidden: true
                            },
                            popper: {
                              sx: {
                                '& .MuiChartsTooltip-paper': {
                                  border: '1px solid grey',
                                  transform: 'translateY(-80px)',
                                },
                              },
                            },
                          }}
                        />
                      </ChartContainer>
                    </>
                  ) : (
                    <Alert severity="info">No review data available for the selected time frame.</Alert>
                  )}
                </Box>

                {reviewSummary && reviewSummary.rating !== undefined && (
                  <Box sx={{ mb: 4, mt: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                      <SmartToyIcon color="primary" sx={{ fontSize: 28 }} />
                      <Typography variant="subtitle1" fontWeight={600}>
                        AI-Powered Review Analysis
                      </Typography>
                    </Box>
                    <UpdateReminder>
                      <Chip
                        size="small"
                        label={"Auto-updates everyweek"}
                        variant="outlined"
                        sx={{ ml: 0 }}
                        color="primary"
                      />
          
                      <Typography variant="body2" color="text.secondary">
                        Next update available: {nextUpdateTime ? new Intl.DateTimeFormat('en-US', {
                          month: 'short',
                          day: 'numeric'
                        }).format(nextUpdateTime) : 'Loading...'}
                      </Typography>
                      <UpdateIcon color="action" fontSize="small" />
                    </UpdateReminder>
                    <Box>
                      {isAnalysisLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                          <CircularProgress />
                        </Box>
                      ) : analysisError ? (
                        <Alert severity="error" sx={{ mb: 2 }}>{analysisError}</Alert>
                      ) : (
                        <ReviewAnalysis analysis={analysisData?.analysis} />
                      )}
                    </Box>
                  </Box>
                )}
              </Box>
            </Fade>
          </Container>
        </StyledPaper>
      </InsightContainer>
    </AppTheme>
  );
};

export default ReviewInsight;
